<script setup lang="ts" generic="T extends Record<string, unknown>">
import { onBeforeMount, ref, watch } from "vue";
import { FilterOperator } from "@primevue/core/api";
import type { DataTableFilterMeta, DataTableRowContextMenuEvent } from "primevue/datatable";
import type { MenuItem } from "primevue/menuitem";
import { useConfirm } from "src/composables/useConfirm";
import { useLayout } from "src/composables/useLayout";
import type { DataTableFilterType } from "src/types/data-table";
import type { NestedKeysOf } from "src/types/dot-notation-keys";
import { useRoute } from "vue-router";
import { onMounted } from "vue";
import { useLocalStorage } from "@vueuse/core";
import type { ChildEntity } from "src/types/entity-schema";

const emits = defineEmits<{
  "click:create": [void];
  "click:delete": [value: T];
  "click:edit": [value: T];
  "click:details": [value: T];
  "click:child-entity": [childEntityName: string, item: T];
  "page": [event: { page: number; rows: number; first: number; pageCount: number }];
}>();

const filterType = defineModel<DataTableFilterType>("filterType");
const searchQuery = defineModel<string | null>("searchQuery", { default: null });
const first = defineModel<number>("first", { default: 0 });
const { data, loading, filters, globalFilters, uniqueKey, actions, lazy, totalRecords } = defineProps<{
  uniqueKey: string;
  data: T[] | null | undefined;
  loading: boolean;
  filters: DataTableFilterMeta;
  globalFilters: Array<NestedKeysOf<T>>;
  lazy?: boolean;
  totalRecords?: number;
  hideHeader?: boolean;
  title?: string;
  childEntities?: ChildEntity[];
  actions: {
    create: boolean;
    delete: boolean;
    edit: boolean;
    view: boolean;
    details: boolean;
  };
}>();

const { confirm } = useConfirm();
const { tableSizeClass } = useLayout();
const route = useRoute();

const refreshKey = ref(0);
const selectedItems = ref<T[]>([]);
const selectedItem = ref<T | null>(null);
const localFilters = ref<DataTableFilterMeta>({});
const dataTableRef = ref(null);
const expandedRows = ref({});
const contextMenuRef = ref();
const contextMenuActions = ref<MenuItem[]>([]);

onBeforeMount(() => {
  clearFilters();
  updateContextMenuActions();
});

function updateContextMenuActions() {
  contextMenuActions.value = [];
  if (actions.edit) {
    contextMenuActions.value.push({
      label: "Edit",
      icon: "pi pi-fw pi-pencil",
      command: () => {
        emits("click:edit", selectedItem.value as T);
      },
    });
  }
  if (actions.delete) {
    contextMenuActions.value.push({
      label: "Delete",
      icon: "pi pi-fw pi-times",
      command: (event) => onClickDelete(event.originalEvent, selectedItem.value as T),
    });
  }
}

function onClickChildEntity(routeName: string, item: T) {
  emits("click:child-entity", routeName, item);
}

function onClickDetails(item: T) {
  emits("click:details", item);
}

function onClickEdit(item: T) {
  emits("click:edit", item);
}

function onClickDelete(event: Event, item: T) {
  const { onAccept } = confirm(event);

  onAccept(() => {
    emits("click:delete", item);
  });
}

function onClickCreate() {
  if (!actions.create) {
    return;
  }

  emits("click:create");
}

function onClickExportCsv() {
  dataTableRef.value?.exportCSV();
}

function onClickDeleteSelected() {
  if (!actions.delete) {
    return;
  }
}

function onRowContextMenu(event: DataTableRowContextMenuEvent) {
  contextMenuRef.value.show(event.originalEvent);
}

function onPage(event: { page: number; rows: number; first: number; pageCount: number }) {
  emits("page", event);
}
function resetTable() {
  localFilters.value = {
    ...filters,
  };
  localStorage.removeItem(uniqueKey);

  refreshKey.value += 1;
}

function clearFilters() {
  localFilters.value = {
    ...filters,
  };

  if (filterType.value === "advanced") {
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const key in localFilters.value) {
      // For each key, set it to the advanced filter structure
      localFilters.value[key] = {
        operator: FilterOperator.OR, // Set OR operator as default
        constraints: [
          // Use existing matchMode and value as a constraint
          {
            value: localFilters.value[key].value,
            matchMode: localFilters.value[key].matchMode,
          },
        ],
      };
    }
  }
}

onMounted(() => {
  const globalFilter = localFilters.value?.["global"];
  if (globalFilter && typeof globalFilter === "object" && "value" in globalFilter && globalFilter.value) {
    searchQuery.value = globalFilter.value as string;
    globalFilter.value = null;
  }

  if (route.query.search) {
    searchQuery.value = route.query.search as string;
  }
});
</script>

<template>
  <div class="p-0" :key="refreshKey">
    <div class="p-4 text-2xl font-bold" v-if="title">
      {{ title }}
    </div>
    <ContextMenu ref="contextMenuRef" :model="contextMenuActions" @hide="selectedItem = null" />
    <DataTable
      ref="dataTableRef"
      v-model:filters="localFilters"
      v-model:first="first"
      v-model:contextMenuSelection="selectedItem"
      class="border-round"
      :class="`p-datatable-${tableSizeClass}`"
      striped-rows
      :show-gridlines="false"
      :value="data ?? []"
      :loading="loading"
      data-key="id"
      :filter-display="filterType === 'simple' ? 'row' : 'menu'"
      :paginator="true"
      :lazy="lazy"
      :total-records="totalRecords"
      resizable-columns
      reorderable-columns
      :rows="100"
      :rows-per-page-options="[5, 10, 25, 50, 100, 500]"
      :global-filter-fields="globalFilters"
      :state-key="uniqueKey"
      state-storage="local"
      context-menu
      @row-contextmenu="onRowContextMenu"
      @page="onPage"
      v-model:expandedRows="expandedRows"
    >
      <template #header>
        <DataTableHeader
          v-if="!hideHeader"
          v-model:search-query="searchQuery"
          v-model:filters="filterType"
          :delete-disabled="false"
          :delete="actions.delete"
          :create="actions.create"
          @update:filters="clearFilters"
          @click:export="onClickExportCsv"
          @click:create="onClickCreate"
          @click:delete="onClickDeleteSelected"
          @click:clear-filters="clearFilters"
          @click:reset-table="resetTable"
          class="rounded"
        >
          <template #left>
            <slot name="header-left" />
          </template>
          <template #right>
            <slot name="header-right" />
          </template>
        </DataTableHeader>
      </template>
      <slot name="columns" />

      <Column
        v-if="actions.edit || actions.delete || actions.view || childEntities"
        :exportable="false"
        body-style="text-align: end; overflow: visible"
        column-key="actions"
      >
        <template #body="slotProps">
          <Button
            v-if="actions.edit"
            icon="pi pi-pencil"
            outlined
            rounded
            class="mr-2"
            size="small"
            @click="onClickEdit(slotProps.data)"
          />
          <Button
            v-if="actions.delete"
            icon="pi pi-trash"
            outlined
            rounded
            size="small"
            severity="danger"
            @click="onClickDelete($event, slotProps.data)"
          />
          <Button
            v-if="actions.details"
            icon="pi pi-eye"
            outlined
            rounded
            class="mr-2"
            size="small"
            @click="onClickDetails(slotProps.data)"
          >
          </Button>
          <Button
            v-for="childEntity in childEntities"
            :key="childEntity.name"
            icon="pi pi-eye"
            outlined
            rounded
            class="mr-2"
            size="small"
            @click="onClickChildEntity(childEntity.route.name, slotProps.data)"
          >
            {{ childEntity.name }}
          </Button>
        </template>
      </Column>

      <template #expansion="slotProps">
        <slot name="expansion" :data="slotProps.data" />
      </template>
    </DataTable>
  </div>
</template>
